import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import {
  FiAlertCircle,
  FiCamera,
  FiChevronDown,
  FiMic,
  FiPlayCircle,
} from 'react-icons/fi';
import logo from '../assets/img/logo.png';
import { interviewService } from '../services/interviewService';
import http, { getUserFacingError } from '../services/http';
import { useInterview } from '../context/InterviewContext';
import { InterviewQuestion } from '../context/InterviewContext';
import {
  createInterviewTraceId,
  interviewPerformanceNow,
  trackInterviewDuration,
} from '../utils/interviewObservability';
import { stageInterviewMediaHandoff } from '../utils/interviewMediaHandoff';
import { readAuthUserId } from '../services/sessionStorage';
import { reconcileInterviewTechnicalInterruptionIntents } from '../utils/interviewTechnicalInterruption';
import {
  getOrCreateInterviewStartupPreparation,
  InterviewEntryStartIntent,
  resolveLostStartResponseRecovery,
} from '../utils/interviewStartupPreparation';

const AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: 1,
};

const MIC_SPEAKING_ON_THRESHOLD = 0.016;
const MIC_SPEAKING_OFF_THRESHOLD = 0.009;

type DeviceOptions = {
  cameras: MediaDeviceInfo[];
  microphones: MediaDeviceInfo[];
};

const buildMediaConstraints = (cameraDeviceId?: string, microphoneDeviceId?: string): MediaStreamConstraints => {
  const audio: MediaTrackConstraints = { ...AUDIO_CONSTRAINTS };
  if (microphoneDeviceId) {
    audio.deviceId = { exact: microphoneDeviceId };
  }

  return {
    video: cameraDeviceId ? { deviceId: { exact: cameraDeviceId } } : true,
    audio,
  };
};

const resolveMediaAccessErrorMessage = (err: any, permissionBlockedMessage: string) => {
  if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
    return permissionBlockedMessage;
  }
  if (err?.name === 'NotFoundError' || err?.name === 'DevicesNotFoundError') {
    return 'Kamera atau mikrofon tidak ditemukan. Pastikan perangkat terhubung, lalu klik Coba Lagi.';
  }
  if (err?.name === 'OverconstrainedError' || err?.name === 'ConstraintNotSatisfiedError') {
    return 'Perangkat yang dipilih tidak tersedia. Pilih kamera/mikrofon lain atau klik Coba Lagi.';
  }
  return 'Izin kamera/mikrofon gagal dicek. Mohon izinkan akses di browser Anda lalu coba lagi.';
};

const resolveInitialJobTitle = (routeState: any) => {
  const title = String(routeState?.jobTitle || routeState?.job?.title || routeState?.position || '').trim();
  return title;
};

const resolveCandidateNames = () => {
  if (typeof window === 'undefined') return { identityName: '', displayName: 'Kamu' };

  try {
    const rawUser = window.localStorage.getItem('authUser') || window.localStorage.getItem('user');
    if (!rawUser) return { identityName: '', displayName: 'Kamu' };
    const user = JSON.parse(rawUser);
    const identityCandidates = [
      user?.name,
      user?.full_name,
      user?.fullname,
      user?.display_name,
      user?.username,
    ];
    const identityName = identityCandidates
      .map((item) => String(item || '').trim())
      .find(Boolean) || '';
    const displayName = identityName || String(user?.email || '').trim() || 'Kamu';
    return { identityName, displayName };
  } catch (_) {
    return { identityName: '', displayName: 'Kamu' };
  }
};

const InterviewEntry: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { bindInterviewScope, setInterviewId, setJobId, setOutsourceId, setQuestions, setStatus } = useInterview();

  const outsourceId: number | null = state?.outsourceId ?? null;
  const jobIdNum = jobId ? Number(jobId) : null;
  const [jobTitle, setJobTitle] = useState(() => resolveInitialJobTitle(state));
  const candidateNames = useMemo(() => resolveCandidateNames(), []);
  const candidateDisplayName = candidateNames.displayName;
  const candidateIdentityName = String(state?.candidateName || '').trim() || candidateNames.identityName;
  const videoPreviewRef = useRef<HTMLVideoElement>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const autoPermissionRequestedRef = useRef(false);
  const startupTraceIdRef = useRef(createInterviewTraceId('startup'));
  const preparedInterviewStatusRef = useRef<string | null>(null);
  const audioMeterRef = useRef<{
    context: AudioContext | null;
    source: MediaStreamAudioSourceNode | null;
    analyser: AnalyserNode | null;
    rafId: number | null;
  }>({
    context: null,
    source: null,
    analyser: null,
    rafId: null,
  });
  const micSpeakingRef = useRef(false);

  const [step, setStep] = useState<'info' | 'checking' | 'ready' | 'error'>('checking');
  const [permError, setPermError] = useState('');
  const [startLoading, setStartLoading] = useState(false);
  const [startError, setStartError] = useState('');
  const [preparationState, setPreparationState] = useState<'preparing' | 'ready' | 'recoverable'>('preparing');
  const [confidenceConsent, setConfidenceConsent] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [micReady, setMicReady] = useState(false);
  const [micSpeaking, setMicSpeaking] = useState(false);
  const [deviceLabels, setDeviceLabels] = useState({ camera: 'Kamera', microphone: 'Mikrofon' });
  const [deviceOptions, setDeviceOptions] = useState<DeviceOptions>({ cameras: [], microphones: [] });
  const [selectedCameraId, setSelectedCameraId] = useState('');
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState('');

  const reconcileStoredTechnicalInterruptions = useCallback(async () => {
    const userId = readAuthUserId();
    if (!userId) return;
    const outcomes = await reconcileInterviewTechnicalInterruptionIntents(
      userId,
      (intent) => interviewService.technicalInterruption(
        intent.interviewId,
        { reason_code: intent.reasonCode, checkpoint: intent.checkpoint },
      ),
    );
    if (outcomes.some((outcome) => !outcome.reconciled)) {
      throw new Error('TECHNICAL_INTERRUPTION_RECONCILIATION_PENDING');
    }
  }, []);

  const permissionBlockedMessage =
    'Izin kamera/mikrofon sedang diblokir di browser. Klik ikon gembok/kamera di address bar, ubah ke Allow, lalu klik Coba Lagi.';

  const setMicSpeakingState = useCallback((nextValue: boolean) => {
    if (micSpeakingRef.current === nextValue) return;
    micSpeakingRef.current = nextValue;
    setMicSpeaking(nextValue);
  }, []);

  const stopAudioMeter = useCallback(() => {
    const meter = audioMeterRef.current;
    if (meter.rafId !== null) {
      cancelAnimationFrame(meter.rafId);
    }
    try { meter.source?.disconnect(); } catch (_) {}
    try { meter.analyser?.disconnect(); } catch (_) {}
    void meter.context?.close().catch(() => undefined);
    audioMeterRef.current = {
      context: null,
      source: null,
      analyser: null,
      rafId: null,
    };
    setMicSpeakingState(false);
  }, [setMicSpeakingState]);

  const stopPreviewStream = useCallback(() => {
    stopAudioMeter();
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
    setCameraReady(false);
    setMicReady(false);
    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = null;
    }
  }, [stopAudioMeter]);

  const handoffPreviewStream = useCallback(() => {
    const stream = mediaStreamRef.current;
    if (!stageInterviewMediaHandoff(stream)) {
      stopPreviewStream();
      return false;
    }

    stopAudioMeter();
    mediaStreamRef.current = null;
    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = null;
    }
    return true;
  }, [stopAudioMeter, stopPreviewStream]);

  const refreshDeviceReadiness = useCallback((stream: MediaStream | null) => {
    const videoTrack = stream?.getVideoTracks().find((track) => track.readyState === 'live' && track.enabled);
    const audioTrack = stream?.getAudioTracks().find((track) => track.readyState === 'live' && track.enabled);

    setCameraReady(Boolean(videoTrack));
    setMicReady(Boolean(audioTrack));
    setDeviceLabels({
      camera: videoTrack?.label || 'Kamera aktif',
      microphone: audioTrack?.label || 'Mikrofon aktif',
    });
  }, []);

  const refreshAvailableDevices = useCallback(async (stream?: MediaStream | null) => {
    if (!navigator.mediaDevices?.enumerateDevices) return;

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((device) => device.kind === 'videoinput');
      const microphones = devices.filter((device) => device.kind === 'audioinput');
      const activeCameraId = stream?.getVideoTracks()[0]?.getSettings().deviceId || '';
      const activeMicrophoneId = stream?.getAudioTracks()[0]?.getSettings().deviceId || '';

      setDeviceOptions({ cameras, microphones });
      setSelectedCameraId((current) => {
        if (activeCameraId) return activeCameraId;
        if (current && cameras.some((device) => device.deviceId === current)) return current;
        return cameras[0]?.deviceId || '';
      });
      setSelectedMicrophoneId((current) => {
        if (activeMicrophoneId) return activeMicrophoneId;
        if (current && microphones.some((device) => device.deviceId === current)) return current;
        return microphones[0]?.deviceId || '';
      });
    } catch (_) {
      // Browser/device enumeration can fail before permission is granted.
    }
  }, []);

  const startAudioMeter = useCallback(async (stream: MediaStream) => {
    stopAudioMeter();

    const hasLiveAudio = () => stream.getAudioTracks().some((track) => (
      track.readyState === 'live' && track.enabled && !track.muted
    ));

    if (!hasLiveAudio()) {
      setMicSpeakingState(false);
      return;
    }

    const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextCtor) {
      setMicSpeakingState(false);
      return;
    }

    try {
      const context = new AudioContextCtor();
      if (context.state === 'suspended') {
        await context.resume().catch(() => undefined);
      }

      const source = context.createMediaStreamSource(stream);
      const analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);

      audioMeterRef.current = {
        context,
        source,
        analyser,
        rafId: null,
      };

      const buffer = new Uint8Array(analyser.fftSize);
      const sample = () => {
        if (audioMeterRef.current.analyser !== analyser) return;

        if (!hasLiveAudio()) {
          setMicSpeakingState(false);
          audioMeterRef.current.rafId = window.requestAnimationFrame(sample);
          return;
        }

        analyser.getByteTimeDomainData(buffer);
        let energy = 0;
        for (let index = 0; index < buffer.length; index += 1) {
          const centered = (buffer[index] - 128) / 128;
          energy += centered * centered;
        }

        const rms = Math.sqrt(energy / buffer.length);
        const nextSpeaking = micSpeakingRef.current
          ? rms > MIC_SPEAKING_OFF_THRESHOLD
          : rms > MIC_SPEAKING_ON_THRESHOLD;
        setMicSpeakingState(nextSpeaking);
        audioMeterRef.current.rafId = window.requestAnimationFrame(sample);
      };

      sample();
    } catch (_) {
      setMicSpeakingState(false);
    }
  }, [setMicSpeakingState, stopAudioMeter]);

  const attachPreviewStream = useCallback((stream: MediaStream) => {
    mediaStreamRef.current = stream;
    stream.getTracks().forEach((track) => {
      track.onended = () => refreshDeviceReadiness(mediaStreamRef.current);
      track.onmute = () => refreshDeviceReadiness(mediaStreamRef.current);
      track.onunmute = () => refreshDeviceReadiness(mediaStreamRef.current);
    });
    refreshDeviceReadiness(stream);

    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = stream;
      void videoPreviewRef.current.play().catch(() => undefined);
    }

    void startAudioMeter(stream);
  }, [refreshDeviceReadiness, startAudioMeter]);

  const openPreviewStream = useCallback(async (cameraDeviceId?: string, microphoneDeviceId?: string) => {
    const stream = await navigator.mediaDevices.getUserMedia(
      buildMediaConstraints(cameraDeviceId, microphoneDeviceId),
    );
    attachPreviewStream(stream);
    setStep('ready');
    await refreshAvailableDevices(stream);
  }, [attachPreviewStream, refreshAvailableDevices]);

  React.useEffect(() => {
    if (!jobIdNum || !outsourceId) return;
    bindInterviewScope({ jobId: jobIdNum, outsourceId });
  }, [bindInterviewScope, jobIdNum, outsourceId]);

  React.useEffect(() => {
    if (!jobIdNum || !outsourceId) return undefined;

    let cancelled = false;
    const requestStartedAt = interviewPerformanceNow();
    const startupContext = { traceId: startupTraceIdRef.current };
    preparedInterviewStatusRef.current = null;
    setPreparationState('preparing');

    const preparation = getOrCreateInterviewStartupPreparation({
      outsourceId,
      jobId: jobIdNum,
      prepare: async () => {
        await reconcileStoredTechnicalInterruptions();
        return interviewService.prepare(outsourceId, jobIdNum, startupContext);
      },
    });

    preparation.promise
      .then((response) => {
        preparedInterviewStatusRef.current = String(response.data?.data?.interview_status || '').trim() || null;
        if (!preparation.reused) {
          trackInterviewDuration('startup_prepare_request_ms', requestStartedAt, {
            ...startupContext,
            outcome: 'success',
            httpStatus: Number(response.status) || null,
          });
        }
        if (!cancelled) {
          setPreparationState(response.data?.data?.preparation_status === 'ready' ? 'ready' : 'recoverable');
        }
      })
      .catch((error: any) => {
        if (!preparation.reused) {
          trackInterviewDuration('startup_prepare_request_ms', requestStartedAt, {
            ...startupContext,
            outcome: 'error',
            httpStatus: Number(error?.response?.status) || null,
          });
        }
        if (!cancelled) setPreparationState('recoverable');
      });

    return () => {
      cancelled = true;
    };
  }, [jobIdNum, outsourceId, reconcileStoredTechnicalInterruptions]);

  React.useEffect(() => {
    if (jobTitle || !jobIdNum) return;

    let isMounted = true;
    http.get(`/jobs/${jobIdNum}`)
      .then((response) => {
        if (!isMounted) return;
        const title = String(response.data?.data?.title || '').trim();
        if (title) setJobTitle(title);
      })
      .catch(() => undefined);

    return () => {
      isMounted = false;
    };
  }, [jobIdNum, jobTitle]);

  React.useEffect(() => {
    if (step === 'ready' && videoPreviewRef.current && mediaStreamRef.current) {
      videoPreviewRef.current.srcObject = mediaStreamRef.current;
      void videoPreviewRef.current.play().catch(() => undefined);
    }
  }, [step]);

  React.useEffect(() => stopPreviewStream, [stopPreviewStream]);

  const canStartInterview = useMemo(
    () => step === 'ready' && cameraReady && micReady && confidenceConsent && !startLoading,
    [cameraReady, confidenceConsent, micReady, startLoading, step],
  );

  const getPermissionState = useCallback(async (name: 'camera' | 'microphone'): Promise<'granted' | 'denied' | 'prompt' | 'unsupported'> => {
    try {
      if (!navigator.permissions?.query) return 'unsupported';
      const result = await navigator.permissions.query({ name: name as PermissionName });
      return result.state;
    } catch {
      return 'unsupported';
    }
  }, []);

  const checkPermission = useCallback(async () => {
    setStep('checking');
    setPermError('');
    setStartError('');
    stopPreviewStream();

    if (!navigator.mediaDevices?.getUserMedia) {
      setPermError('Browser Anda tidak mendukung akses kamera/mikrofon, atau halaman perlu dibuka via HTTPS.');
      setStep('error');
      return;
    }

    const [cameraState, micState] = await Promise.all([
      getPermissionState('camera'),
      getPermissionState('microphone'),
    ]);

    if (cameraState === 'denied' || micState === 'denied') {
      setPermError(permissionBlockedMessage);
      setStep('error');
      return;
    }

    try {
      await openPreviewStream(selectedCameraId, selectedMicrophoneId);
    } catch (err: any) {
      setPermError(resolveMediaAccessErrorMessage(err, permissionBlockedMessage));
      setStep('error');
    }
  }, [getPermissionState, openPreviewStream, permissionBlockedMessage, selectedCameraId, selectedMicrophoneId, stopPreviewStream]);

  const handleDeviceChange = useCallback(async (kind: 'camera' | 'microphone', deviceId: string) => {
    const nextCameraId = kind === 'camera' ? deviceId : selectedCameraId;
    const nextMicrophoneId = kind === 'microphone' ? deviceId : selectedMicrophoneId;

    setStartError('');
    setPermError('');
    setSelectedCameraId(nextCameraId);
    setSelectedMicrophoneId(nextMicrophoneId);

    if (!navigator.mediaDevices?.getUserMedia || step !== 'ready') return;

    setStep('checking');
    stopPreviewStream();

    try {
      await openPreviewStream(nextCameraId, nextMicrophoneId);
    } catch (err: any) {
      setPermError(resolveMediaAccessErrorMessage(err, permissionBlockedMessage));
      setStep('error');
    }
  }, [openPreviewStream, permissionBlockedMessage, selectedCameraId, selectedMicrophoneId, step, stopPreviewStream]);

  React.useEffect(() => {
    if (!jobIdNum || !outsourceId || autoPermissionRequestedRef.current) return;
    autoPermissionRequestedRef.current = true;
    void checkPermission();
  }, [checkPermission, jobIdNum, outsourceId]);

  React.useEffect(() => {
    if (!navigator.mediaDevices?.addEventListener) return undefined;

    const handleDeviceListChange = () => {
      void refreshAvailableDevices(mediaStreamRef.current);
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceListChange);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceListChange);
    };
  }, [refreshAvailableDevices]);

  const handleStart = async () => {
    if (!outsourceId || !jobIdNum) {
      setStartError('Sesi interview tidak valid. Silakan buka ulang dari halaman lamaran.');
      return;
    }

    const activeOutsourceId = outsourceId;
    const activeJobId = jobIdNum;

    if (!cameraReady || !micReady) {
      setStartError('Kamera dan mikrofon harus aktif terlebih dahulu sebelum memulai interview.');
      return;
    }
    if (!confidenceConsent) {
      setStartError('Anda harus menyetujui confidence signal terlebih dahulu sebelum memulai interview.');
      return;
    }
    setStartLoading(true);
    setStartError('');
    const startupTraceId = startupTraceIdRef.current;
    const startIntent: InterviewEntryStartIntent = state?.sessionMode === 'resume'
      || preparedInterviewStatusRef.current === 'in_progress'
      ? 'resume_existing'
      : 'activate_fresh';
    const startupStartedAt = interviewPerformanceNow();
    const startupContext = { traceId: startupTraceId };
    const startRequestStartedAt = interviewPerformanceNow();
    let startRequestSettled = false;
    try {
      await reconcileStoredTechnicalInterruptions();
      const res = await interviewService.start(activeOutsourceId, activeJobId, true, startupContext);
      trackInterviewDuration('start_request_ms', startRequestStartedAt, {
        ...startupContext,
        outcome: 'success',
        httpStatus: Number(res.status) || null,
      });
      startRequestSettled = true;
      const iv = res.data?.data;
      if (!iv) throw new Error('Respons tidak valid');

      const resolvedId: number = iv.interview_id ?? iv.id;
      if (!resolvedId) throw new Error('ID interview tidak valid');

      setInterviewId(resolvedId);
      setJobId(activeJobId);
      setOutsourceId(activeOutsourceId);
      setQuestions((iv.questions as InterviewQuestion[]) || []);
      setStatus('in_progress');
      const sessionMode = iv?.progress?.session_mode === 'resume' ? 'resume' : 'new';

      const reusePreparedMedia = handoffPreviewStream();
      navigate(`/interview/session/${activeJobId}`, {
        state: {
          interviewId: resolvedId,
          outsourceId: activeOutsourceId,
          jobId: activeJobId,
          candidateName: String(iv.candidate_name || '').trim() || candidateIdentityName,
          jobTitle,
          sessionMode,
          cameraDeviceId: selectedCameraId || undefined,
          microphoneDeviceId: selectedMicrophoneId || undefined,
          reusePreparedMedia,
          observabilityTraceId: startupTraceId,
          startupStartedAt,
        },
      });
    } catch (err: any) {
      if (!startRequestSettled) {
        trackInterviewDuration('start_request_ms', startRequestStartedAt, {
          ...startupContext,
          outcome: 'error',
          httpStatus: Number(err?.response?.status) || null,
        });
      }
      const isTimeout = String(err?.code || '').toUpperCase() === 'ECONNABORTED';
      if (isTimeout) {
        const resumeLookupStartedAt = interviewPerformanceNow();
        try {
          const resumeRes = await interviewService.getByApplication(activeOutsourceId, activeJobId, startupContext);
          trackInterviewDuration('startup_resume_lookup_ms', resumeLookupStartedAt, {
            ...startupContext,
            outcome: 'success',
            httpStatus: Number(resumeRes.status) || null,
          });
          const payload = resumeRes.data?.data;
          const existingInterview = payload?.interview;
          const existingQuestions = Array.isArray(payload?.questions) ? payload.questions : [];
          const recoveryDecision = resolveLostStartResponseRecovery({
            interviewStatus: existingInterview?.status,
            answeredCount: payload?.progress?.answered_count,
            backendSessionMode: payload?.progress?.session_mode,
            startIntent,
            recoveryCandidateName: payload?.candidate_name || existingInterview?.candidate_name,
            entryCandidateName: candidateIdentityName,
          });

          if (existingInterview?.id && recoveryDecision.canRecover && existingQuestions.length) {
            setInterviewId(existingInterview.id);
            setJobId(activeJobId);
            setOutsourceId(activeOutsourceId);
            setQuestions(existingQuestions as InterviewQuestion[]);
            setStatus('in_progress');

            const reusePreparedMedia = handoffPreviewStream();
            navigate(`/interview/session/${activeJobId}`, {
              state: {
                interviewId: existingInterview.id,
                outsourceId: activeOutsourceId,
                jobId: activeJobId,
                candidateName: recoveryDecision.candidateName,
                jobTitle,
                sessionMode: recoveryDecision.sessionMode,
                cameraDeviceId: selectedCameraId || undefined,
                microphoneDeviceId: selectedMicrophoneId || undefined,
                reusePreparedMedia,
                observabilityTraceId: startupTraceId,
                startupStartedAt,
              },
            });
            return;
          }
        } catch (resumeError: any) {
          trackInterviewDuration('startup_resume_lookup_ms', resumeLookupStartedAt, {
            ...startupContext,
            outcome: 'error',
            httpStatus: Number(resumeError?.response?.status) || null,
          });
        }
      }

      setStartError(getUserFacingError(err, 'Gagal memulai interview. Silakan coba lagi.').message);
      setStartLoading(false);
    }
  };

  if (!outsourceId || !jobIdNum) {
    return (
      <div className="iv-entry">
        <div className="iv-entry__shell">
          <div className="iv-entry__panel iv-entry__panel--error">
            <h2>Sesi tidak valid</h2>
            <p>Silakan daftar lowongan terlebih dahulu.</p>
            <button className="iv-btn btn-brand" onClick={() => navigate('/lowongan-kerja')}>
              Lihat Lowongan
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="iv-entry">
      <div className="iv-entry__shell">
        <div className="iv-entry__panel">
          <div className="iv-entry__brand">
            <button
              className="iv-entry__brand-link"
              type="button"
              onClick={() => navigate('/status-lamaran')}
              aria-label="Kembali ke status lamaran"
            >
              <img className="iv-entry__brand-logo" src={logo} alt="TEMA" />
            </button>
            <div className="iv-entry__brand-copy">
              <span>Lowongan Pekerjaan</span>
              <strong>Screening Interview</strong>
            </div>
          </div>

          <div className="iv-entry__layout">
            <section className="iv-entry__preview-card" aria-label="Preview kamera">
              <div className={`iv-entry__video-frame${cameraReady ? ' iv-entry__video-frame--ready' : ''}`}>
                <video ref={videoPreviewRef} className="iv-entry__video" autoPlay muted playsInline />
                {!cameraReady && (
                  <div className="iv-entry__video-placeholder">
                    {step === 'checking' ? <Spinner animation="border" /> : <FiCamera />}
                    <span>{step === 'checking' ? 'Mengaktifkan kamera...' : 'Preview kamera belum aktif'}</span>
                  </div>
                )}
                <div className="iv-entry__video-name">
                  <span>{candidateDisplayName}</span>
                  <span className={`iv-entry__sound-wave${micSpeaking ? ' iv-entry__sound-wave--active' : ''}`} aria-hidden="true">
                    <i />
                    <i />
                    <i />
                    <i />
                  </span>
                </div>
                <div className={`iv-entry__video-live${cameraReady && micReady ? ' iv-entry__video-live--on' : ''}`}>
                  {cameraReady && micReady ? 'Camera & mic on' : 'Waiting for devices'}
                </div>
              </div>

              <div className="iv-entry__device-bar">
                <label className={`iv-entry__device-pill${cameraReady ? ' iv-entry__device-pill--ready' : ''}`}>
                  <FiCamera />
                  <select
                    aria-label="Pilih kamera"
                    value={selectedCameraId}
                    onChange={(event) => void handleDeviceChange('camera', event.target.value)}
                    disabled={step === 'checking' || deviceOptions.cameras.length <= 1}
                  >
                    {deviceOptions.cameras.length ? (
                      deviceOptions.cameras.map((device, index) => (
                        <option key={device.deviceId || `camera-${index}`} value={device.deviceId}>
                          {device.label || `Kamera ${index + 1}`}
                        </option>
                      ))
                    ) : (
                      <option value="">{deviceLabels.camera}</option>
                    )}
                  </select>
                  {deviceOptions.cameras.length > 1 && <FiChevronDown className="iv-entry__device-chevron" />}
                </label>
                <label className={`iv-entry__device-pill${micReady ? ' iv-entry__device-pill--ready' : ''}`}>
                  <FiMic />
                  <select
                    aria-label="Pilih mikrofon"
                    value={selectedMicrophoneId}
                    onChange={(event) => void handleDeviceChange('microphone', event.target.value)}
                    disabled={step === 'checking' || deviceOptions.microphones.length <= 1}
                  >
                    {deviceOptions.microphones.length ? (
                      deviceOptions.microphones.map((device, index) => (
                        <option key={device.deviceId || `microphone-${index}`} value={device.deviceId}>
                          {device.label || `Mikrofon ${index + 1}`}
                        </option>
                      ))
                    ) : (
                      <option value="">{deviceLabels.microphone}</option>
                    )}
                  </select>
                  {deviceOptions.microphones.length > 1 && <FiChevronDown className="iv-entry__device-chevron" />}
                </label>
              </div>
            </section>

            <aside className="iv-entry__join-card">
              <div className="iv-entry__panel-header">
                <div className="iv-entry__eyebrow iv-entry__eyebrow--panel">Posisi Yang Dilamar</div>
                <h1 className="iv-entry__title">{jobTitle || '-'}</h1>
                <p className="iv-entry__subtitle">
                  Kamera dan mikrofon akan aktif otomatis. Setelah keduanya aktif dan persetujuan dicentang, interview bisa dimulai.
                </p>
              </div>

              <div className="iv-entry__summary">
                <div className="iv-entry__summary-item">
                  <span className="iv-entry__summary-label">Format</span>
                  <strong>5 pertanyaan utama</strong>
                </div>
                <div className="iv-entry__summary-item">
                  <span className="iv-entry__summary-label">Lanjutan</span>
                  <strong>Maks. 3 follow-up</strong>
                </div>
                <div className="iv-entry__summary-item">
                  <span className="iv-entry__summary-label">Durasi</span>
                  <strong>15-20 menit</strong>
                </div>
              </div>

              {(step === 'info' || step === 'checking') && (
                <div className="iv-entry__module iv-entry__module--checking">
                  <div className="iv-entry__checking">
                    <Spinner animation="border" size="sm" />
                    {step === 'checking' ? 'Mengaktifkan kamera dan mikrofon...' : 'Menyiapkan akses perangkat...'}
                  </div>
                </div>
              )}

              {step === 'error' && (
                <div className="iv-entry__module iv-entry__module--error">
                  <div className="iv-entry__module-header">
                    <div className="iv-entry__module-icon iv-entry__module-icon--error"><FiAlertCircle /></div>
                    <div>
                      <div className="iv-entry__module-title">Akses perangkat belum siap</div>
                      <p className="iv-entry__module-copy">{permError}</p>
                    </div>
                  </div>
                  <button className="btn btn-outline-brand w-100 mt-2" onClick={checkPermission}>
                    Coba Lagi
                  </button>
                </div>
              )}

              {step === 'ready' && (
                <div className="iv-entry__ready">
                  <div className="">
                    <label className="iv-entry__consent-label" htmlFor="confidence-consent">
                      <input
                        id="confidence-consent"
                        className="iv-entry__consent-input"
                        type="checkbox"
                        checked={confidenceConsent}
                        onChange={(event) => setConfidenceConsent(event.target.checked)}
                      />
                      <span className="iv-entry__consent-text">
                        Saya setuju sistem menganalisis sinyal confidence dari suara rekaman jawaban untuk membantu penilaian interview.
                      </span>
                    </label>
                  </div>
                </div>
              )}

              <p className="small text-muted text-center mb-2">
                {preparationState === 'preparing'
                  ? 'Pertanyaan interview sedang disiapkan sambil perangkat diperiksa.'
                  : preparationState === 'ready'
                  ? 'Pertanyaan interview sudah siap.'
                  : 'Persiapan awal akan dilanjutkan secara aman saat interview dimulai.'}
              </p>
              {startError && <p className="iv-entry__start-error">{startError}</p>}
              <button
                className="iv-btn btn-brand iv-btn--lg iv-entry__cta"
                onClick={handleStart}
                disabled={!canStartInterview}
              >
                {startLoading ? (
                  <>
                    <Spinner animation="border" size="sm" /> {preparationState === 'preparing' ? 'Menyiapkan pertanyaan...' : 'Memulai...'}
                  </>
                ) : (
                  <>
                    <span>Mulai Interview</span>
                    <FiPlayCircle />
                  </>
                )}
              </button>
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewEntry;
